import React, { lazy } from "react";
import { Navigate, useLocation } from "react-router-dom";
// DO NOT REMOVE: This is the centralized route configuration.
// All pages must be imported lazily to ensure optimal performance.

const HomePage = lazy(() => import("@/pages/home"));
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const MyWorkPage = lazy(() => import("@/pages/my-work"));
const MyWorkDetailPage = lazy(() => import("@/pages/my-work-detail"));
const SetupWizardPage = lazy(() => import("@/pages/setup/SetupWizardPage"));
const SetupRoadmapPage = lazy(() => import("@/pages/setup/SetupRoadmapPage"));
const RiskAssessmentMatrixPage = lazy(() =>
    import("@/pages/definitions/risk/risk-assessment-matrix"),
);
const BiaImpactScalePage = lazy(() => import("@/pages/definitions/risk/bia-impact-scale"));
const CiaMatrixDefinitionsPage = lazy(() => import("@/pages/definitions/risk/cia-matrix"));
const RiskAnalysisRedirectPage = lazy(() => import("@/pages/definitions/risk/RiskAnalysisRedirectPage"));
const KVKKCompanyPage = lazy(() => import("@/pages/definitions/kvkk/kvkk-company"));
const KVKKCategoryPage = lazy(() => import("@/pages/definitions/kvkk/kvkk-category"));
const ProcessingPurposePage = lazy(() => import("@/pages/definitions/kvkk/processing-purpose"));
const SubjectPersonGroupPage = lazy(() => import("@/pages/definitions/kvkk/subject-person-group"));
const CollectionMethodPage = lazy(() => import("@/pages/definitions/kvkk/collection-method"));
const LegalReasonPage = lazy(() => import("@/pages/definitions/kvkk/legal-reason"));
const BuyerGroupRedirectPage = lazy(() => import("@/pages/definitions/kvkk/buyer-group-redirect"));
const TransferTypePage = lazy(() => import("@/pages/definitions/kvkk/international-transfer/transfer-types"));
const InternationalTransferPage = lazy(() => import("@/pages/definitions/kvkk/international-transfer/index"));
const InternationalTransferMechanismRedirectPage = lazy(() => import("@/pages/definitions/kvkk/international-transfer/mechanism-redirect"));
const KvkkRecipientGroupPage = lazy(() => import("@/pages/definitions/kvkk/recipient-group"));
const KvkkDataDisposalMethodPage = lazy(() => import("@/pages/definitions/kvkk/data-disposal-method"));
const KvkkDataSourcePage = lazy(() => import("@/pages/definitions/kvkk/data-source"));
const KvkkPrivacyPrinciplePage = lazy(() => import("@/pages/definitions/kvkk/privacy-principle"));
const KvkkSecurityMeasurePage = lazy(() => import("@/pages/definitions/kvkk/security-measure"));
const KVKKRedirectPage = lazy(() => import("@/pages/definitions/kvkk/KVKKRedirectPage"));
const ExplicitConsentMethodPage = lazy(() => import("@/pages/definitions/obtaining-explicit-consent/explicit-consent-method"));
const ExplicitConsentReasonPage = lazy(() => import("@/pages/definitions/obtaining-explicit-consent/explicit-consent-reason"));
const ExplicitConsentRedirectPage = lazy(() => import("@/pages/definitions/obtaining-explicit-consent/ExplicitConsentRedirectPage"));
const ApplicationMethodPage = lazy(() => import("@/pages/definitions/application-tracking/application-method"));
const RelationshipInformationRedirectPage = lazy(() => import("@/pages/definitions/application-tracking/relationship-information-redirect"));
const ApplicationRequestInformationPage = lazy(() => import("@/pages/definitions/application-tracking/application-request-information"));
const ApplicationTrackingRedirectPage = lazy(() => import("@/pages/definitions/application-tracking/ApplicationTrackingRedirectPage"));
const DocumentTypePage = lazy(() => import("@/pages/definitions/document/document-type"));
const ClassificationPage = lazy(() => import("@/pages/definitions/document/classification"));
const DocumentRedirectPage = lazy(() => import("@/pages/definitions/document/DocumentRedirectPage"));
const VendorsPage = lazy(() => import("@/pages/vendor-management/vendors"));
const VendorProfilePage = lazy(() => import("@/pages/vendor-management/vendor-profile"));
const UnitsPage = lazy(() => import("@/pages/definitions/organization/units"));
const ProcessPage = lazy(() => import("@/pages/definitions/organization/process"));
const TeamPage = lazy(() => import("@/pages/definitions/organization/team"));
const TeamDetailPage = lazy(() => import("@/pages/definitions/organization/team-detail"));
const DutyDefinitionsPage = lazy(() => import("@/pages/definitions/organization/duty-definitions"));
const RootCausePage = lazy(() => import("@/pages/definitions/organization/root-cause"));
const PlatformMfaSettingsPage = lazy(() => import("@/pages/platform/mfa-settings"));
const CommunicationDeliveryHistoryPage = lazy(() => import("@/pages/platform/communication-delivery-history"));
const NotificationTemplatesPage = lazy(() => import("@/pages/platform/notification-templates"));
const NotificationPoliciesPage = lazy(() => import("@/pages/platform/notification-policies"));
const NotificationPolicyWorkspacePage = lazy(() => import("@/pages/platform/notification-policy-workspace"));
const NotificationPolicyRecipientsPage = lazy(() => import("@/pages/platform/notification-policy-recipients"));
const GeneralSystemRedirectPage = lazy(() => import("@/pages/definitions/organization/GeneralSystemRedirectPage"));
const RegulatorySourcesPage = lazy(() => import("@/pages/definitions/compliance/regulatory-sources"));
const RegulatoryClausesPage = lazy(() => import("@/pages/definitions/compliance/regulatory-clauses"));
const ComplianceObligationsPage = lazy(() => import("@/pages/definitions/compliance/compliance-obligations"));
const ComplianceFrameworksPage = lazy(() => import("@/pages/definitions/compliance/compliance-frameworks"));
const ComplianceRedirectPage = lazy(() => import("@/pages/definitions/compliance/ComplianceRedirectPage"));
const PrivacyProcessingActivitiesRedirectPage = lazy(() => import("@/pages/definitions/privacy/processing-activities-redirect"));
const PrivacyRetentionPeriodsPage = lazy(() => import("@/pages/definitions/privacy/retention-periods"));
const PrivacyDataSubjectTypesRedirectPage = lazy(() => import("@/pages/definitions/privacy/data-subject-types-redirect"));
const PrivacyLegalBasisRedirectPage = lazy(() => import("@/pages/definitions/privacy/legal-basis-redirect"));
const PrivacyDpiaTemplatesPage = lazy(() => import("@/pages/definitions/privacy/dpia-templates"));
const PrivacyRedirectPage = lazy(() => import("@/pages/definitions/privacy/PrivacyRedirectPage"));
const VendorAssessmentsPage = lazy(() => import("@/pages/vendor-management/vendor-assessments"));
const IntegrationConnectorsPage = lazy(() => import("@/pages/definitions/integrations/connectors"));
const TenantDirectoryPage = lazy(() => import("@/pages/definitions/integrations/tenant-directory"));
const IdentityProvidersPage = lazy(() => import("@/pages/definitions/integrations/identity-providers"));
const GovernanceReportDefinitionsPage = lazy(() => import("@/pages/definitions/governance/report-definitions"));
const ReportBrandingPage = lazy(() => import("@/pages/definitions/report-branding"));
const SystemSurveysPage = lazy(() => import("@/pages/definitions/forms/surveys"));
const SurveyResponsesPage = lazy(() => import("@/pages/definitions/forms/survey-responses"));
// AutomationsPage removed (Plan Faz 1.6). Replaced by Workflow Designer (purpose='automation') (Plan Faz 4-5).
const AssetGroupPage = lazy(() => import("@/pages/asset-inventory-management/asset-group"));

const AssetCategoryPage = lazy(() => import("@/pages/asset-inventory-management/asset-category"));
const AssetsPage = lazy(() => import("@/pages/asset-inventory-management/assets"));
const AssetDetailPage = lazy(() => import("@/pages/asset-inventory-management/AssetDetail"));
const PdInventoryPage = lazy(() => import("@/pages/asset-inventory-management/pd-inventory"));
const LicenseManagementPage = lazy(() => import("@/pages/asset-inventory-management/license-management"));
const DisposalManagementPage = lazy(() => import("@/pages/asset-inventory-management/disposal-management"));
const MaintenanceManagementPage = lazy(() => import("@/pages/asset-inventory-management/maintenance-management"));
const AssetLifecycleEventsPage = lazy(() =>
    import("@/pages/asset-inventory-management/asset-lifecycle-events"),
);
const FormsPage = lazy(() => import("@/pages/report-operations/forms"));
const DocumentPage = lazy(() => import("@/pages/report-operations/document"));
const DocumentDetailPage = lazy(() => import("@/pages/report-operations/DocumentDetail"));
const DetailedReportDownloadPage = lazy(() => import("@/pages/report-operations/DetailedReportDownload"));
const ReportStudioPage = lazy(() => import("@/pages/report-operations/report-studio"));
const ReportArchivePage = lazy(() => import("@/pages/report-operations/report-archive"));
const ReportTestLabPage = lazy(() => import("@/pages/report-operations/report-test-lab"));
const ContextPage = lazy(() => import("@/pages/context-management/context"));
const CommunicationPage = lazy(() => import("@/pages/context-management/communication"));
const ObjectivePage = lazy(() => import("@/pages/isms-controls/objective"));
const ObjectiveDetailPage = lazy(() => import("@/pages/isms-controls/objective-detail"));
const CompetencePage = lazy(() => import("@/pages/isms-controls/competence"));
const TrainingPage = lazy(() => import("@/pages/isms-controls/training/training"));
const ISO27001TrainingRedirectPage = lazy(() => import("@/pages/isms-controls/training/ISO27001TrainingRedirectPage"));
const MyTrainingPage = lazy(() => import("@/pages/isms-controls/training/my-training"));
const TrainingVideoPage = lazy(() => import("@/pages/isms-controls/training/video"));
const TrainingEvaluationPage = lazy(() => import("@/pages/isms-controls/training/evaluation"));
const TrainingEvaluationResultsPage = lazy(() => import("@/pages/isms-controls/training/evaluation-results"));
const TrainerEvaluationPage = lazy(() => import("@/pages/isms-controls/training/trainer-evaluation"));
const TrainingEvaluationFeedbackPage = lazy(() => import("@/pages/isms-controls/training/training-evaluation-feedback"));
const InstructorEvaluationFeedbackPage = lazy(() => import("@/pages/isms-controls/training/instructor-evaluation-feedback"));
const MeetingPage = lazy(() => import("@/pages/context-management/meeting"));
const InterestedPartiesPage = lazy(() => import("@/pages/context-management/interested-parties"));
const ScopeDocumentPage = lazy(() => import("@/pages/context-management/scope-document"));
const ObtainingExplicitConsentPage = lazy(() => import("@/pages/gdpr-operations/obtaining-explicit-consent"));
const ExplicitConsentDetail = lazy(() => import("@/pages/gdpr-operations/ExplicitConsentDetail"));
const ApplicationTrackingPage = lazy(() => import("@/pages/gdpr-operations/application-tracking"));
const DataSubjectRequestPage = lazy(() => import("@/pages/gdpr-operations/data-subject-request"));
const DataBreachPage = lazy(() => import("@/pages/gdpr-operations/data-breach"));
const ConsentRecordPage = lazy(() => import("@/pages/gdpr-operations/consent-record"));
const PrivacyNoticePage = lazy(() => import("@/pages/gdpr-operations/privacy-notice"));
const DpiaAssessmentPage = lazy(() => import("@/pages/gdpr-operations/dpia-assessment"));
const DisposalPage = lazy(() => import("@/pages/gdpr-operations/disposal"));
const VerbisPage = lazy(() => import("@/pages/gdpr-operations/verbis"));
const DpaRegistryPage = lazy(() => import("@/pages/gdpr-operations/dpa-registry"));
const GdprDashboardPage = lazy(() => import("@/pages/gdpr-operations/dashboard"));
const CookieInventoryPage = lazy(() => import("@/pages/gdpr-operations/cookie-inventory"));
const PublicDsrRequestPage = lazy(() => import("@/pages/public/dsr-request"));
const PublicDocumentsPage = lazy(() => import("@/pages/public/public-documents"));
const PublicDocumentPage = lazy(() => import("@/pages/public/public-document"));
const RiskMonitoringPage = lazy(() => import("@/pages/risk-management/risk-monitoring"));
const RiskRegisterPage = lazy(() => import("@/pages/risk-management/risk-register"));
const RiskRegisterDetail = lazy(() => import("@/pages/risk-management/risk-register/RiskRegisterDetail"));
const RiskTreatmentPage = lazy(() => import("@/pages/risk-management/risk-treatment"));
const ControlPage = lazy(() => import("@/pages/isms-controls/control"));
const ControlDetailPage = lazy(() => import("@/pages/isms-controls/ControlDetail"));
const AssessmentQuestionsPage = lazy(() => import("@/pages/isms-controls/assessment-questions"));
const AssessmentPage = lazy(() => import("@/pages/audit-compliance/audit-assessment"));
const AuditCalendarPage = lazy(() => import("@/pages/audit-compliance/audit-program"));
const MyAuditsPage = lazy(() => import("@/pages/audit-compliance/my-audits"));
const AuditComplianceCapaPage = lazy(() => import("@/pages/audit-compliance/capa"));
const IsmsControlsSoaPage = lazy(() => import("@/pages/isms-controls/soa"));
const AuditComplianceKpiDashboardPage = lazy(() => import("@/pages/audit-compliance/kpi-dashboard"));
const ItsmSlaManagementPage = lazy(() => import("@/pages/itsm/sla-management"));
const ItsmServiceCatalogPage = lazy(() => import("@/pages/itsm/service-catalog"));
const ItsmHubPage = lazy(() => import("@/pages/itsm/hub"));
const ItsmChangeRequestPage = lazy(() => import("@/pages/itsm/change-request"));
const ItsmChangeCasePage = lazy(() => import("@/pages/itsm/ChangeCase"));
const ItsmProblemPage = lazy(() => import("@/pages/itsm/problem"));
const ItsmProblemCasePage = lazy(() => import("@/pages/itsm/ProblemCase"));
const ItsmSupplierContractsPage = lazy(() => import("@/pages/itsm/supplier-contracts"));
const IsmsControlsThreatIntelligencePage = lazy(() => import("@/pages/isms-controls/threat-intelligence"));
const IsmsControlsVulnerabilityPage = lazy(() => import("@/pages/isms-controls/vulnerability"));
const ResidualRiskAcceptancePage = lazy(() => import("@/pages/risk-management/residual-risk-acceptance"));
const RiskOpportunitiesPage = lazy(() => import("@/pages/risk-management/risk-opportunities"));
const IncidentsPage = lazy(() => import("@/pages/incident-management/incidents"));
const IncidentTemplatesPage = lazy(() => import("@/pages/incident-management/incident-templates"));
const IncidentMonitoringPage = lazy(() => import("@/pages/incident-management/incident-monitoring"));
const IncidentDetailPage = lazy(() => import("@/pages/incident-management/IncidentDetail"));
const ActionsPage = lazy(() => import("@/pages/incident-management/actions"));
const RootPublicLoginRedirect = () => React.createElement(Navigate, { to: "/login", replace: true });
const createCanonicalRedirect = (pathname) => function CanonicalRedirect() {
  const location = useLocation();
  return React.createElement(Navigate, {
    to: { pathname, search: location.search, hash: location.hash },
    state: location.state,
    replace: true,
  });
};
const createPathPrefixRedirect = (fromPrefix, toPrefix) => function PathPrefixRedirect() {
  const location = useLocation();
  const pathname = location.pathname.startsWith(fromPrefix)
    ? `${toPrefix}${location.pathname.slice(fromPrefix.length)}`
    : toPrefix;
  return React.createElement(Navigate, {
    to: { pathname, search: location.search, hash: location.hash },
    state: location.state,
    replace: true,
  });
};
const LegacyIsoTrainingSubtreeRedirectPage = createPathPrefixRedirect('/isms-controls/training', '/training');
const LegacyIsoTrainingRootRedirectPage = createCanonicalRedirect('/training');
const LegacyIsoTrainingListRedirectPage = createCanonicalRedirect('/training/list');
const LegacyIsoTrainingMyTrainingRedirectPage = createCanonicalRedirect('/training/my-training');
const LegacyIsoTrainingEvaluationRedirectPage = createCanonicalRedirect('/training/my-training');
const LegacyIsoCompetenceRedirectPage = createCanonicalRedirect('/training/competence');
const LegacyMailTemplateRedirectPage = createCanonicalRedirect('/platform/communications/templates');
const LegacyCommunicationProvidersRedirectPage = createCanonicalRedirect('/platform/communications/channels');
const LegacyScheduledDistributionRedirectPage = createCanonicalRedirect('/reports/distributions');
const LegacyReportArchiveRedirectPage = createCanonicalRedirect('/reports/archive');
const LegacyReportProfilesRedirectPage = createCanonicalRedirect('/reports/studio');
const LegacyReportTemplatesRedirectPage = createCanonicalRedirect('/reports/studio');
const LegacyReportDesignRedirectPage = createCanonicalRedirect('/definitions/report-branding');
const LegacyRiskScaleRedirectPage = createCanonicalRedirect('/definitions/scale/risk-assessment-matrix');
const LegacyCiaScaleRedirectPage = createCanonicalRedirect('/definitions/scale/cia-matrix');
const UserManagementPage = lazy(() => import("@/pages/user-operations/user-management"));
const RoleManagementPage = lazy(() => import("@/pages/user-operations/role-management"));
const SystemLogsPage = lazy(() => import("@/pages/user-operations/system-logs"));
const AboutSgrcPage = lazy(() => import("@/pages/user-manual/about-sgrc"));
const DesignSystemPage = lazy(() => import("@/pages/user-manual/design-system/index"));
const PatchNotesPage = lazy(() => import("@/pages/user-manual/patch-notes"));
const BIGDESDefinitionsCircularArticleDefinitionsPage = lazy(() => import("@/pages/bigdes-operations/definitions/circular-article-definitions"));
const BIGDESDefinitionsMeasuresPage = lazy(() => import("@/pages/bigdes-operations/definitions/measures"));
const BIGDESDefinitionsRedirectPage = lazy(() => import("@/pages/bigdes-operations/definitions/BIGDESDefinitionsRedirectPage"));
const BIGDESAssetGroupEvaluationPage = lazy(() => import("@/pages/bigdes-operations/asset-group-criticality-degree-determination/asset-group-evaluation"));
const BIGDESSurveyFillingPage = lazy(() => import("@/pages/bigdes-operations/asset-group-criticality-degree-determination/survey-filling"));
const BIGDESAssetGroupCriticalityRedirectPage = lazy(() => import("@/pages/bigdes-operations/asset-group-criticality-degree-determination/BIGDESAssetGroupCriticalityRedirectPage"));
const BIGDESMeasureApplicabilityPage = lazy(() => import("@/pages/bigdes-operations/measure-applicability"));
const BIGDESCurrentSituationAnalysisPage = lazy(() => import("@/pages/bigdes-operations/current-situation-analysis"));
const BIGDESCurrentSituationAnalysisDetailPage = lazy(() => import("@/pages/bigdes-operations/current-situation-analysis-detail"));
const BIGDESRoadMapPage = lazy(() => import("@/pages/bigdes-operations/road-map"));
const BIGDESCompensatoryControlPage = lazy(() => import("@/pages/bigdes-operations/compensatory-control"));
const ReportArchiveRedirectPage = createCanonicalRedirect('/reports/archive');
const BIGDESApplicationProcessEvaluationPage = lazy(() => import("@/pages/bigdes-operations/audit/application-process-evaluation"));
const BIGDESCurrentSituationEvaluationPage = lazy(() => import("@/pages/bigdes-operations/audit/current-situation-evaluation"));
const BIGDESCurrentSituationEvaluationDetailPage = lazy(() => import("@/pages/bigdes-operations/audit/current-situation-evaluation-detail"));
const BIGDESAuditReportsPage = lazy(() => import("@/pages/bigdes-operations/audit/audit-reports"));
const BIGDESAuditRedirectPage = lazy(() => import("@/pages/bigdes-operations/audit/BIGDESAuditRedirectPage"));
const QueueDashboardPage = lazy(() => import("@/pages/definitions/queues"));
// Legacy approval/workflow admin pages removed (Plan Faz 1.6).
// Replaced by Workflow Designer (Plan Faz 4), SystemLog Workflow Audit tab (Plan Faz 9).
const PartyTemplatesPage = lazy(() => import("@/pages/platform/party-templates"));
const AssignmentTemplatesPage = lazy(() => import("@/pages/platform/assignment-templates"));
const CollectionCampaignsPage = lazy(() => import("@/pages/platform/collection-campaigns"));
const ThirdPartyApplicationsPage = lazy(() => import("@/pages/platform/third-party-applications"));
const ThirdPartyMarketplacePage = lazy(() => import("@/pages/platform/integrations/marketplace"));
const IntegrationConnectionOverviewPage = lazy(() => import("@/pages/platform/integrations/connection-overview"));
const IntegrationConnectionExplorerPage = lazy(() => import("@/pages/platform/integrations/connection-explorer"));
const IntegrationConnectionMappingsPage = lazy(() => import("@/pages/platform/integrations/connection-mappings"));
const IntegrationConnectionSchedulesPage = lazy(() => import("@/pages/platform/integrations/connection-schedules"));
const IntegrationConnectionRunsPage = lazy(() => import("@/pages/platform/integrations/connection-runs"));
const IntegrationConnectionAutomationsPage = lazy(() => import("@/pages/platform/integrations/connection-automations"));
const IntegrationConnectionSettingsPage = lazy(() => import("@/pages/platform/integrations/connection-settings"));
const WorkflowTemplatesPage = lazy(() => import("@/pages/platform/workflow-templates"));
const WorkflowDesignerPage = lazy(() => import("@/pages/platform/workflow-designer"));
const BusinessCalendarPage = lazy(() => import("@/pages/platform/business-calendar"));
const ScheduledReportDistributionPage = lazy(() => import("@/pages/platform/scheduled-report-distribution"));
const PendingWorkflowOperationsPage = lazy(() => import("@/pages/admin/pending-workflow-operations"));
const MyDelegationsPage = lazy(() => import("@/pages/user-operations/delegations"));
const AdminDelegationsPage = lazy(() => import("@/pages/admin/delegations"));
const DeadLetterQueuePage = lazy(() => import("@/pages/admin/dead-letter-queue"));
const AdminBackupsPage = lazy(() => import("@/pages/admin/backups"));
const TailorabilityPage = lazy(() => import("@/pages/platform/tailorability"));
const SeedPolicyPage = lazy(() => import("@/pages/platform/seed-policy"));
const WebhooksAdminPage = lazy(() => import("@/pages/admin/webhooks"));
const CommunicationProvidersPage = lazy(() => import("@/pages/settings/communication-providers"));
const NotificationPreferencesPage = lazy(() => import("@/pages/settings"));
const TenantCompliancePage = lazy(() => import("@/pages/admin/compliance-frameworks"));
const DataExportImportPage = lazy(() => import("@/pages/admin/data-export-import"));
const TenantOnboardingPage = lazy(() => import("@/pages/admin/tenant-onboarding"));


// BCM Pages
const BiaPage = lazy(() => import("@/pages/bcm/bia"));
const BiaDetailPage = lazy(() => import("@/pages/bcm/BiaDetailPage"));
const BcpPage = lazy(() => import("@/pages/bcm/bcp"));
const BcpDetailPage = lazy(() => import("@/pages/bcm/BcpDetail"));
const CrisisEventPage = lazy(() => import("@/pages/bcm/crisis-event"));
const CrisisEventDetailPage = lazy(() => import("@/pages/bcm/CrisisEventDetail"));
const ExercisesPage = lazy(() => import("@/pages/bcm/exercise"));
const ExercisesDetailPage = lazy(() => import("@/pages/bcm/ExerciseDetail"));

// Ticketing Pages
const TicketingPage = lazy(() => import("@/pages/ticketing/index"));
const TicketDetailPage = lazy(() => import("@/pages/ticketing/TicketDetail"));
const TicketTemplateManagerPage = lazy(() => import("@/pages/ticketing/TicketTemplateManager"));
const TicketSettingsPage = lazy(() => import("@/pages/ticketing/TicketSettings"));
const TicketMyTasksPage = lazy(() => import("@/pages/ticketing/MyTasks"));
const TicketCreatePage = lazy(() => import("@/pages/ticketing/TicketCreate"));
const TicketDashboardPage = lazy(() => import("@/pages/ticketing/TicketDashboard"));

export const routeConfigs = [
  // Dashboard & setup
  {
    path: "/home",
    component: HomePage,
    pageKey: "home",
    protected: true,
  },
  {
    path: "/dashboard",
    component: DashboardPage,
    pageKey: "home",
    protected: true,
  },
  {
    path: "/my-work",
    component: MyWorkPage,
    protected: true,
  },
  {
    path: "/my-work/:kind/:id",
    component: MyWorkDetailPage,
    protected: true,
  },
  {
    path: "/setup",
    component: SetupWizardPage,
    protected: true,
  },
  {
    path: "/setup-roadmap",
    component: SetupRoadmapPage,
    protected: true,
  },

  // Definitions (reference data) — canonical `/definitions/*`
  {
    path: "/definitions",
    component: GeneralSystemRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk",
    component: KVKKRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/company",
    component: KVKKCompanyPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/category",
    component: KVKKCategoryPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/processing-purpose",
    component: ProcessingPurposePage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/subject-person-group",
    component: SubjectPersonGroupPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/collection-method",
    component: CollectionMethodPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/legal-reason",
    component: LegalReasonPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/buyer-group",
    component: BuyerGroupRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/transfer-types",
    component: TransferTypePage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/ssc",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/board-notification",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/bcr",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/explicit-consent",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/adequacy-decision",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/undertaking",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/public-agreement",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer/incidental",
    component: InternationalTransferMechanismRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/international-transfer",
    component: InternationalTransferPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/recipient-group",
    component: KvkkRecipientGroupPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/data-disposal-method",
    component: KvkkDataDisposalMethodPage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/data-source",
    component: KvkkDataSourcePage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/privacy-principle",
    component: KvkkPrivacyPrinciplePage,
    protected: true,
  },
  {
    path: "/definitions/kvkk/security-measure",
    component: KvkkSecurityMeasurePage,
    protected: true,
  },

  {
    path: "/definitions/risk-analysis",
    component: RiskAnalysisRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/risk-analysis/*",
    component: RiskAnalysisRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/scale",
    component: RiskAnalysisRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/scale/risk-assessment-matrix",
    component: RiskAssessmentMatrixPage,
    protected: true,
  },
  {
    path: "/definitions/scale/bia-impact-scale",
    component: BiaImpactScalePage,
    protected: true,
  },
  {
    path: "/definitions/scale/impact",
    component: LegacyRiskScaleRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/scale/probability",
    component: LegacyRiskScaleRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/scale/cia-confidentiality",
    component: LegacyCiaScaleRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/scale/cia-integrity",
    component: LegacyCiaScaleRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/scale/cia-availability",
    component: LegacyCiaScaleRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/scale/cia-matrix",
    component: CiaMatrixDefinitionsPage,
    protected: true,
  },
  {
    path: "/definitions/obtaining-explicit-consent",
    component: ExplicitConsentRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/obtaining-explicit-consent/method",
    component: ExplicitConsentMethodPage,
    protected: true,
  },
  {
    path: "/definitions/obtaining-explicit-consent/reason",
    component: ExplicitConsentReasonPage,
    protected: true,
  },

  {
    path: "/definitions/application-tracking",
    component: ApplicationTrackingRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/application-tracking/application-method",
    component: ApplicationMethodPage,
    protected: true,
  },
  {
    path: "/definitions/application-tracking/relationship-information",
    component: RelationshipInformationRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/application-tracking/application-request-information",
    component: ApplicationRequestInformationPage,
    protected: true,
  },

  {
    path: "/definitions/document",
    component: DocumentRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/document/document-type",
    component: DocumentTypePage,
    protected: true,
  },
  {
    path: "/definitions/document/classification",
    component: ClassificationPage,
    protected: true,
  },

  {
    path: "/vendor-management/vendors",
    component: VendorsPage,
    protected: true,
  },
  {
    path: "/vendor-management/vendors/:vendorId",
    component: VendorProfilePage,
    protected: true,
  },
  {
    path: "/vendor-management/vendor-assessments",
    component: VendorAssessmentsPage,
    protected: true,
  },

  {
    path: "/definitions/surveys",
    component: SystemSurveysPage,
    protected: true,
  },
  {
    path: "/definitions/survey-responses",
    component: SurveyResponsesPage,
    protected: true,
  },

  // /definitions/automations removed (Plan Faz 1.6). Replaced by Workflow Designer (Plan Faz 4-5).

  {
    path: "/definitions/organization",
    component: GeneralSystemRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/organization/units",
    component: UnitsPage,
    protected: true,
  },
  {
    path: "/definitions/organization/process",
    component: ProcessPage,
    protected: true,
  },
  {
    path: "/definitions/organization/team/:id",
    component: TeamDetailPage,
    protected: true,
  },
  {
    path: "/definitions/organization/team",
    component: TeamPage,
    protected: true,
  },
  {
    path: "/definitions/organization/duty-definitions",
    component: DutyDefinitionsPage,
    protected: true,
  },
  {
    path: "/definitions/organization/root-cause",
    component: RootCausePage,
    protected: true,
  },
  {
    path: "/definitions/organization/mail-template",
    component: LegacyMailTemplateRedirectPage,
    protected: true,
  },

  {
    path: "/definitions/compliance",
    component: ComplianceRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/compliance/regulatory-sources",
    component: RegulatorySourcesPage,
    protected: true,
  },
  {
    path: "/definitions/compliance/clauses",
    component: RegulatoryClausesPage,
    protected: true,
  },
  {
    path: "/definitions/compliance/obligations",
    component: ComplianceObligationsPage,
    protected: true,
  },
  {
    path: "/definitions/compliance/frameworks",
    component: ComplianceFrameworksPage,
    protected: true,
  },
  {
    path: "/definitions/compliance/report-definitions",
    component: GovernanceReportDefinitionsPage,
    protected: true,
  },

  {
    path: "/definitions/privacy",
    component: PrivacyRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/privacy/processing-activities",
    component: PrivacyProcessingActivitiesRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/privacy/retention-periods",
    component: PrivacyRetentionPeriodsPage,
    protected: true,
  },
  {
    path: "/definitions/privacy/data-subject-types",
    component: PrivacyDataSubjectTypesRedirectPage,
    protected: true,
  },
  {
    path: "/definitions/privacy/dpia-templates",
    component: PrivacyDpiaTemplatesPage,
    protected: true,
  },
  {
    path: "/definitions/privacy/legal-basis",
    component: PrivacyLegalBasisRedirectPage,
    protected: true,
  },

  {
    path: "/definitions/integrations/connectors",
    component: IntegrationConnectorsPage,
    protected: true,
  },
  {
    path: "/definitions/integrations/tenant-directory",
    component: TenantDirectoryPage,
    protected: true,
  },
  {
    path: "/definitions/integrations/identity-providers",
    component: IdentityProvidersPage,
    protected: true,
    pageKey: "definitionsIdentityProviders",
  },
  {
    path: "/definitions/report-branding",
    component: ReportBrandingPage,
    protected: true,
    pageKey: "definitionsReportDesign",
  },
  {
    path: "/definitions/report-design",
    component: LegacyReportDesignRedirectPage,
    protected: true,
  },

  // Asset inventory management
  {
    path: "/asset-inventory-management/asset-group",
    component: AssetGroupPage,
    protected: true,
  },

  {
    path: "/asset-inventory-management/asset-category",
    component: AssetCategoryPage,
    protected: true,
  },
  {
    path: "/asset-inventory-management/assets",
    component: AssetsPage,
    protected: true,
  },
  {
    path: "/asset-inventory-management/assets/:id",
    component: AssetDetailPage,
    protected: true,
  },
  {
    path: "/asset-inventory-management/pd-inventory",
    component: PdInventoryPage,
    protected: true,
  },
  {
    path: "/asset-inventory-management/license-management",
    component: LicenseManagementPage,
    protected: true,
  },
  {
    path: "/asset-inventory-management/maintenance-management",
    component: MaintenanceManagementPage,
    protected: true,
  },
  {
    path: "/asset-inventory-management/disposal-management",
    component: DisposalManagementPage,
    protected: true,
  },
  {
    path: "/asset-inventory-management/asset-lifecycle-events",
    component: AssetLifecycleEventsPage,
    protected: true,
  },

  // Report operations
  {
    path: "/report-operations/forms",
    component: FormsPage,
    protected: true,
  },
  {
    path: "/report-operations/document",
    component: DocumentPage,
    protected: true,
  },
  {
    path: "/report-operations/document/:id",
    component: DocumentDetailPage,
    protected: true,
  },
  {
    path: "/reports/studio",
    component: ReportStudioPage,
    protected: true,
    pageKey: "reportReports",
  },
  {
    path: "/reports/profiles",
    component: LegacyReportProfilesRedirectPage,
    protected: true,
  },
  {
    path: "/reports/distributions",
    component: ScheduledReportDistributionPage,
    protected: true,
    pageKey: "platformScheduledReportDistribution",
  },
  {
    path: "/reports/archive",
    component: ReportArchivePage,
    protected: true,
    pageKey: "reportArchive",
  },
  // Intentional direct-only test route; do not add it to sidebar navigation.
  {
    path: "/reports/test-lab",
    component: ReportTestLabPage,
    protected: true,
  },
  {
    path: "/reports/detailed/download",
    component: DetailedReportDownloadPage,
    protected: true,
  },
  {
    path: "/report-operations/reports",
    component: LegacyReportTemplatesRedirectPage,
    protected: true,
  },
  {
    path: "/report-operations/report-archive",
    component: LegacyReportArchiveRedirectPage,
    protected: true,
  },

  // Context management (shared) — canonical
  {
    path: "/context-management/context",
    component: ContextPage,
    protected: true,
  },
  {
    path: "/context-management/communication",
    component: CommunicationPage,
    protected: true,
  },
  {
    path: "/context-management/meeting",
    component: MeetingPage,
    protected: true,
  },
  {
    path: "/context-management/interested-parties",
    component: InterestedPartiesPage,
    protected: true,
  },
  {
    path: "/context-management/scope-document",
    component: ScopeDocumentPage,
    protected: true,
  },
  // ISMS controls — canonical
  {
    path: "/isms-controls/objective",
    component: ObjectivePage,
    protected: true,
  },
  {
    path: "/isms-controls/objective/:id",
    component: ObjectiveDetailPage,
    protected: true,
  },
  {
    path: "/training",
    component: ISO27001TrainingRedirectPage,
    protected: true,
  },
  {
    path: "/training/list",
    component: TrainingPage,
    protected: true,
  },
  {
    path: "/training/my-training",
    component: MyTrainingPage,
    protected: true,
  },
  {
    path: "/training/video/:trainingId",
    component: TrainingVideoPage,
    protected: true,
  },
  {
    path: "/training/evaluation/:userTrainingId",
    component: TrainingEvaluationPage,
    protected: true,
  },
  {
    path: "/training/evaluation/:userTrainingId/results",
    component: TrainingEvaluationResultsPage,
    protected: true,
  },
  {
    path: "/training/competence",
    component: CompetencePage,
    protected: true,
  },
  {
    path: "/training/training-evaluation/:userTrainingId",
    component: TrainingEvaluationFeedbackPage,
    protected: true,
  },
  {
    path: "/training/instructor-evaluation/:userTrainingId",
    component: InstructorEvaluationFeedbackPage,
    protected: true,
  },
  {
    path: "/training/trainer-evaluation/:trainingId",
    component: TrainerEvaluationPage,
    protected: true,
  },
  {
    path: "/training/evaluation",
    component: LegacyIsoTrainingEvaluationRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/competence",
    component: LegacyIsoCompetenceRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training",
    component: LegacyIsoTrainingRootRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/list",
    component: LegacyIsoTrainingListRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/evaluation",
    component: LegacyIsoTrainingEvaluationRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/my-training",
    component: LegacyIsoTrainingMyTrainingRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/video/:trainingId",
    component: LegacyIsoTrainingSubtreeRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/evaluation/:userTrainingId",
    component: LegacyIsoTrainingSubtreeRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/evaluation/:userTrainingId/results",
    component: LegacyIsoTrainingSubtreeRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/training-evaluation/:userTrainingId",
    component: LegacyIsoTrainingSubtreeRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/instructor-evaluation/:userTrainingId",
    component: LegacyIsoTrainingSubtreeRedirectPage,
    protected: true,
  },
  {
    path: "/isms-controls/training/trainer-evaluation/:trainingId",
    component: LegacyIsoTrainingSubtreeRedirectPage,
    protected: true,
  },
  // GDPR operations
  {
    path: "/gdpr-operations/obtaining-explicit-consent",
    component: ObtainingExplicitConsentPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/obtaining-explicit-consent/:id",
    component: ExplicitConsentDetail,
    protected: true,
  },
  {
    path: "/gdpr-operations/application-tracking",
    component: ApplicationTrackingPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/data-subject-request",
    component: DataSubjectRequestPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/data-breach",
    component: DataBreachPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/consent-record",
    component: ConsentRecordPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/privacy-notice",
    component: PrivacyNoticePage,
    protected: true,
  },
  {
    path: "/gdpr-operations/dpia-assessment",
    component: DpiaAssessmentPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/disposal",
    component: DisposalPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/verbis",
    component: VerbisPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/dpa-registry",
    component: DpaRegistryPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/dashboard",
    component: GdprDashboardPage,
    protected: true,
  },
  {
    path: "/gdpr-operations/cookie-inventory",
    component: CookieInventoryPage,
    protected: true,
  },

  // Risk management — canonical
  {
    path: "/risk-management/risk-monitoring",
    component: RiskMonitoringPage,
    protected: true,
  },
  {
    path: "/risk-management/risk-register",
    component: RiskRegisterPage,
    protected: true,
  },
  {
    path: "/risk-management/risk-register/:id",
    component: RiskRegisterDetail,
    protected: true,
  },
  {
    path: "/risk-management/risk-treatment",
    component: RiskTreatmentPage,
    protected: true,
  },
  {
    path: "/risk-management/residual-risk-acceptance",
    component: ResidualRiskAcceptancePage,
    protected: true,
  },
  {
    path: "/risk-management/risk-opportunities",
    component: RiskOpportunitiesPage,
    protected: true,
  },
  // ISMS controls — canonical
  {
    path: "/isms-controls/control",
    component: ControlPage,
    protected: true,
  },
  {
    path: "/isms-controls/control/:id",
    component: ControlDetailPage,
    protected: true,
  },
  {
    path: "/isms-controls/assessment-questions",
    component: AssessmentQuestionsPage,
    protected: true,
  },
  {
    path: "/isms-controls/soa",
    component: IsmsControlsSoaPage,
    protected: true,
  },
  {
    path: "/isms-controls/threat-intelligence",
    component: IsmsControlsThreatIntelligencePage,
    protected: true,
  },
  {
    path: "/isms-controls/vulnerability",
    component: IsmsControlsVulnerabilityPage,
    protected: true,
  },
  // Audit & compliance — canonical
  {
    path: "/audit-compliance/audit-assessment",
    component: AssessmentPage,
    protected: true,
  },
  {
    path: "/audit-compliance/audit-program",
    component: AuditCalendarPage,
    protected: true,
  },
  {
    path: "/audit-compliance/my-audits",
    component: MyAuditsPage,
    protected: true,
  },
  {
    path: "/audit-compliance/capa",
    component: AuditComplianceCapaPage,
    protected: true,
  },
  {
    path: "/audit-compliance/kpi-dashboard",
    component: AuditComplianceKpiDashboardPage,
    protected: true,
  },
  // Incident management
  {
    path: "/incident-management/incidents/monitoring",
    component: IncidentMonitoringPage,
    protected: true,
  },
  {
    path: "/user-manual/about-sgrc",
    component: AboutSgrcPage,
    protected: true,
  },
  {
    path: "/user-manual/design-system",
    component: DesignSystemPage,
    protected: true,
  },
  {
    path: "/user-manual/patch-notes",
    component: PatchNotesPage,
    protected: true,
  },
  {
    path: "/incident-management/incidents/:id",
    component: IncidentDetailPage,
    protected: true,
  },
  {
    path: "/incident-management/incidents",
    component: IncidentsPage,
    protected: true,
  },
  {
    path: "/incident-management/templates",
    component: IncidentTemplatesPage,
    protected: true,
  },
  {
    path: "/incident-management/actions",
    component: ActionsPage,
    protected: true,
  },

  // BIGDES operations - definitions
  {
    path: "/bigdes-operations/definitions",
    component: BIGDESDefinitionsRedirectPage,
    protected: true,
  },
  {
    path: "/bigdes-operations/definitions/circular-article-definitions",
    component: BIGDESDefinitionsCircularArticleDefinitionsPage,
    protected: true,
  },
  {
    path: "/bigdes-operations/definitions/measures",
    component: BIGDESDefinitionsMeasuresPage,
    protected: true,
  },

  // BIGDES operations - asset group criticality degree determination
  {
    path: "/bigdes-operations/asset-group-criticality-degree-determination",
    component: BIGDESAssetGroupCriticalityRedirectPage,
    protected: true,
  },
  {
    path:
      "/bigdes-operations/asset-group-criticality-degree-determination/asset-group-evaluation",
    component: BIGDESAssetGroupEvaluationPage,
    protected: true,
  },
  {
    path:
      "/bigdes-operations/asset-group-criticality-degree-determination/survey-filling",
    component: BIGDESSurveyFillingPage,
    protected: true,
  },

  // BIGDES operations - other
  {
    path: "/bigdes-operations/measure-applicability",
    component: BIGDESMeasureApplicabilityPage,
    protected: true,
    pageKey: "bigdesMeasureApplicability",
  },
  {
    path: "/bigdes-operations/current-situation-analysis",
    component: BIGDESCurrentSituationAnalysisPage,
    protected: true,
    pageKey: "bigdesCurrentSituationAnalysis",
  },
  {
    path: "/bigdes-operations/current-situation-analysis/:id",
    component: BIGDESCurrentSituationAnalysisDetailPage,
    protected: true,
    pageKey: "bigdesCurrentSituationAnalysis",
  },
  {
    path: "/bigdes-operations/road-map",
    component: BIGDESRoadMapPage,
    protected: true,
    pageKey: "bigdesRoadMap",
  },
  {
    path: "/bigdes-operations/compensatory-control",
    component: BIGDESCompensatoryControlPage,
    protected: true,
    pageKey: "bigdesCompensatoryControl",
  },
  {
    path: "/bigdes-operations/bigdes-reports",
    component: ReportArchiveRedirectPage,
    protected: true,
  },

  // BIGDES operations - audit
  {
    path: "/bigdes-operations/audit",
    component: BIGDESAuditRedirectPage,
    protected: true,
  },
  {
    path: "/bigdes-operations/audit/application-process-evaluation",
    component: BIGDESApplicationProcessEvaluationPage,
    protected: true,
  },
  {
    path: "/bigdes-operations/audit/current-situation-evaluation",
    component: BIGDESCurrentSituationEvaluationPage,
    protected: true,
    pageKey: "bigdesCurrentSituationEvaluation",
  },
  {
    path: "/bigdes-operations/audit/current-situation-evaluation/:id",
    component: BIGDESCurrentSituationEvaluationDetailPage,
    protected: true,
    pageKey: "bigdesCurrentSituationEvaluation",
  },
  {
    path: "/bigdes-operations/audit/audit-reports",
    component: BIGDESAuditReportsPage,
    protected: true,
    pageKey: "bigdesAuditReports",
  },

  // BCM Operations
  {
    path: "/bcm/bia",
    component: BiaPage,
    protected: true,
  },
  {
    path: "/bcm/bia/:id",
    component: BiaDetailPage,
    protected: true,
  },
  {
    path: "/bcm/bcp",
    component: BcpPage,
    protected: true,
  },
  {
    path: "/bcm/bcp/:id",
    component: BcpDetailPage,
    protected: true,
  },
  {
    path: "/bcm/crisis-event",
    component: CrisisEventPage,
    protected: true,
  },
  {
    path: "/bcm/crisis-event/:id",
    component: CrisisEventDetailPage,
    protected: true,
  },
  {
    path: "/bcm/exercises",
    component: ExercisesPage,
    protected: true,
  },
  {
    path: "/bcm/exercises/:id",
    component: ExercisesDetailPage,
    protected: true,
  },

  {
    path: "/admin/queues",
    component: QueueDashboardPage,
    protected: true,
  },
  // /platform/workflows, /platform/page-approval-flows, /platform/approval-admin,
  // /platform/approval-audit-log routes removed (Plan Faz 1.6).
  // Replaced by Workflow Designer (Plan Faz 4), SystemLog Workflow Audit tab (Plan Faz 9).
  {
    path: "/platform/party-templates",
    component: PartyTemplatesPage,
    protected: true,
  },
  {
    path: "/platform/assignment-templates",
    component: AssignmentTemplatesPage,
    protected: true,
  },
  {
    path: "/platform/collection-campaigns",
    component: CollectionCampaignsPage,
    pageKey: "collectionCampaigns",
    protected: true,
  },
  {
    path: "/platform/third-party-applications",
    component: ThirdPartyApplicationsPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/marketplace",
    component: ThirdPartyMarketplacePage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/:connectionId/overview",
    component: IntegrationConnectionOverviewPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/:connectionId/explorer",
    component: IntegrationConnectionExplorerPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/:connectionId/mappings",
    component: IntegrationConnectionMappingsPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/:connectionId/schedules",
    component: IntegrationConnectionSchedulesPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/:connectionId/runs",
    component: IntegrationConnectionRunsPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/:connectionId/automations",
    component: IntegrationConnectionAutomationsPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/third-party-applications/:connectionId/settings",
    component: IntegrationConnectionSettingsPage,
    pageKey: "platformThirdPartyApplications",
    protected: true,
  },
  {
    path: "/platform/workflow-templates",
    component: WorkflowTemplatesPage,
    protected: true,
  },
  {
    path: "/platform/workflow-templates/new",
    component: WorkflowDesignerPage,
    protected: true,
  },
  {
    path: "/platform/workflow-templates/:id",
    component: WorkflowDesignerPage,
    protected: true,
  },
  {
    path: "/platform/business-calendar",
    component: BusinessCalendarPage,
    protected: true,
    pageKey: "platformBusinessCalendar",
  },
  {
    path: "/platform/scheduled-report-distribution",
    component: LegacyScheduledDistributionRedirectPage,
    protected: true,
  },
  {
    path: "/admin/pending-workflow-operations",
    component: PendingWorkflowOperationsPage,
    protected: true,
  },
  {
    path: "/user-operations/delegations",
    component: MyDelegationsPage,
    protected: true,
    pageKey: "userOperationsDelegations",
  },
  {
    path: "/admin/delegations",
    component: AdminDelegationsPage,
    protected: true,
    pageKey: "adminDelegations",
  },
  {
    path: "/admin/dead-letter-queue",
    component: DeadLetterQueuePage,
    protected: true,
    pageKey: "adminDeadLetterQueue",
  },
  {
    path: "/admin/backups",
    component: AdminBackupsPage,
    protected: true,
    pageKey: "adminBackup",
  },
  {
    path: "/platform/tailorability",
    component: TailorabilityPage,
    protected: true,
    pageKey: "platformTailorability",
  },
  {
    path: "/platform/seed-policy",
    component: SeedPolicyPage,
    protected: true,
    pageKey: "platformTailorability",
  },
  {
    path: "/admin/webhooks",
    component: WebhooksAdminPage,
    protected: true,
    pageKey: "adminWebhooks",
  },
  {
    path: "/settings/notification-preferences",
    component: NotificationPreferencesPage,
    protected: true,
  },
  {
    path: "/platform/communications/delivery-history",
    component: CommunicationDeliveryHistoryPage,
    protected: true,
  },
  {
    path: "/platform/communications/channels",
    component: CommunicationProvidersPage,
    protected: true,
    pageKey: "communicationProvider",
  },
  {
    path: "/platform/communications/mfa",
    component: PlatformMfaSettingsPage,
    protected: true,
    pageKey: "platformMfaSettings",
  },
  {
    path: "/platform/communications/templates",
    component: NotificationTemplatesPage,
    protected: true,
    pageKey: "definitionsGeneralSystemMailTemplate",
  },
  {
    path: "/platform/communications/notification-policies",
    component: NotificationPoliciesPage,
    protected: true,
    pageKey: "platformNotificationPolicies",
  },
  {
    path: "/platform/communications/notification-policies/events/:eventName",
    component: NotificationPolicyWorkspacePage,
    protected: true,
    pageKey: "platformNotificationPolicies",
  },
  {
    path: "/platform/communications/notification-policies/:policyId/recipients",
    component: NotificationPolicyRecipientsPage,
    protected: true,
    pageKey: "platformNotificationPolicies",
  },
  {
    path: "/admin/communication-providers",
    component: LegacyCommunicationProvidersRedirectPage,
    protected: true,
  },
  {
    path: "/admin/compliance-frameworks",
    component: TenantCompliancePage,
    protected: true,
  },
  {
    path: "/admin/data-export-import",
    component: DataExportImportPage,
    protected: true,
  },
  {
    path: "/admin/tenant-onboarding",
    component: TenantOnboardingPage,
    protected: true,
  },

  // ITSM
  {
    path: "/itsm/hub",
    component: ItsmHubPage,
    protected: true,
  },
  {
    path: "/itsm/service-catalog",
    component: ItsmServiceCatalogPage,
    protected: true,
  },
  {
    path: "/itsm/change-request",
    component: ItsmChangeRequestPage,
    protected: true,
  },
  {
    path: "/itsm/change-request/:id",
    component: ItsmChangeCasePage,
    protected: true,
    pageKey: "itsmChangeRequest",
  },
  {
    path: "/itsm/problem",
    component: ItsmProblemPage,
    protected: true,
  },
  {
    path: "/itsm/problem/:id",
    component: ItsmProblemCasePage,
    protected: true,
    pageKey: "itsmProblem",
  },
  {
    path: "/itsm/sla-management",
    component: ItsmSlaManagementPage,
    protected: true,
  },
  {
    path: "/itsm/supplier-contracts",
    component: ItsmSupplierContractsPage,
    protected: true,
  },
  // Ticketing — static routes MUST come before :id dynamic route
  {
    path: "/ticketing",
    component: TicketingPage,
    protected: true,
  },
  {
    path: "/ticketing/create",
    component: TicketCreatePage,
    protected: true,
  },
  {
    path: "/ticketing/dashboard",
    component: TicketDashboardPage,
    protected: true,
  },
  {
    path: "/ticketing/templates",
    component: TicketTemplateManagerPage,
    protected: true,
  },
  {
    path: "/ticketing/settings",
    component: TicketSettingsPage,
    protected: true,
  },
  {
    path: "/ticketing/my-tasks",
    component: TicketMyTasksPage,
    protected: true,
  },
  {
    path: "/ticketing/:id",
    component: TicketDetailPage,
    protected: true,
  },



  // User operations
  {
    path: "/user-operations/user-management",
    component: UserManagementPage,
    protected: true,
  },
  {
    path: "/user-operations/role-management",
    component: RoleManagementPage,
    protected: true,
  },
  {
    path: "/user-operations/system-logs",
    component: SystemLogsPage,
    protected: true,
  },

  {
    path: "/",
    component: RootPublicLoginRedirect,
    protected: false,
  },
  {
    // KVKK m.11 / Tebliğ — auth'suz veri sahibi başvuru portalı
    path: "/public/dsr/:tenantSlug",
    component: PublicDsrRequestPage,
    protected: false,
  },
  {
    path: "/public-docs",
    component: PublicDocumentsPage,
    protected: false,
  },
  {
    path: "/public-docs/:shareId",
    component: PublicDocumentPage,
    protected: false,
  },
];
